import React from 'react';
import * as Icons from 'lucide-react';
import { Users, Heart, Gem, ArrowRight } from 'lucide-react';
import { DEFAULT_SITE_CONTENT, mergeContent } from '@/constants/defaultSiteContent';

const Hero = ({ onCta, content }) => {
  const c = mergeContent(DEFAULT_SITE_CONTENT.hero, content || {});
  const fallbackPillIcons = [Users, Heart, Gem];

  return (
    <section id="home" className="relative pt-24 sm:pt-32 pb-10 sm:pb-20 overflow-hidden isolate">
      {/* Full-bleed atmospheric background */}
      <div className="absolute inset-0 -z-10">
        <img src={c.bg_image_url} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F5F0E6]/95 via-[#F5F0E6]/70 to-[#0F2E23]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5F0E6]/20 via-transparent to-[#0F2E23]/40" />
      </div>

      <div className="relative max-w-[1360px] mx-auto grid lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] gap-6 lg:gap-12 items-stretch px-4 sm:px-6">
        {/* Text column */}
        <div className="pt-2 sm:pt-8 lg:pr-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F2E23]/10 backdrop-blur border border-[#0F2E23]/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C89B3C]" />
            <span className="text-[10px] tracking-[0.28em] font-semibold text-[#0F2E23]">{c.badge}</span>
          </div>
          <h1 className="mt-4 sm:mt-5 font-display text-[44px] sm:text-[76px] lg:text-[96px] leading-[0.95] tracking-tight text-[#141414]">
            {c.headline_top}<br/>{c.headline_bottom}
          </h1>
          <p className="mt-4 sm:mt-6 text-[10px] sm:text-xs tracking-[0.22em] text-[#141414]/80">
            {c.eyebrow_line1}<br/>
            {c.eyebrow_line2}
          </p>
          <p className="mt-4 sm:mt-6 max-w-lg text-[14px] sm:text-[15px] leading-relaxed text-[#141414]/85">{c.body}</p>
          <button data-testid="hero-primary-cta-button" onClick={onCta} className="pg-glossy mt-6 sm:mt-8 inline-flex items-center gap-3 bg-[#0F2E23] hover:bg-[#153F30] text-[#F5F0E6] pl-5 pr-4 py-3 rounded-md text-[11px] tracking-[0.22em] font-semibold shadow-[0_12px_30px_rgba(15,46,35,0.35)] transition-colors">
            {c.cta_label} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-6 sm:mt-8 flex flex-wrap items-center gap-x-4 gap-y-3 sm:gap-x-8">
            {(c.pills || []).map((p, i) => {
              const IconComp = Icons[p.icon] || fallbackPillIcons[i] || Users;
              return <IconLabel key={i} icon={IconComp} label={p.label} />;
            })}
          </div>
        </div>

        {/* Image column */}
        <div className="relative rounded-xl overflow-hidden h-[300px] sm:min-h-[540px] lg:min-h-[620px] shadow-[0_40px_100px_rgba(15,46,35,0.45)] ring-1 ring-[#0F2E23]/20">
          <img src={c.image_url} alt={c.image_alt || ''} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

          {(c.neon_lines || []).length > 0 && (
            <div className="absolute right-4 bottom-16 sm:bottom-24 hidden sm:block">
              <div className="bg-[#141414]/70 backdrop-blur-sm border border-[#F5C86A]/50 px-4 py-3 rounded-sm">
                <div className="font-display text-[#F5C86A] text-lg leading-none tracking-widest text-center drop-shadow-[0_0_10px_rgba(245,200,106,0.7)]">
                  {c.neon_lines.map((l, i) => <React.Fragment key={i}>{l}{i < c.neon_lines.length - 1 && <br/>}</React.Fragment>)}
                </div>
              </div>
            </div>
          )}

          <div className="absolute right-4 sm:right-6 bottom-3 sm:bottom-4 text-right">
            <div style={{ fontFamily: 'Great Vibes, cursive' }} className="text-[#F5F0E6] text-3xl sm:text-5xl leading-none drop-shadow-[0_4px_10px_rgba(0,0,0,0.6)]">{c.script_text}</div>
            <div className="text-[#F5F0E6]/90 text-[9px] sm:text-[10px] tracking-[0.32em] mt-1">{c.script_sub}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const IconLabel = ({ icon: Icon, label }) => (
  <div className="inline-flex items-center gap-1.5 sm:gap-2">
    <span className="inline-flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-full bg-[#0F2E23] text-[#F5F0E6] shadow-md">
      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
    </span>
    <span className="text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.22em] font-semibold text-[#141414]">{label}</span>
  </div>
);

export default Hero;
